/**
 * SessionProgressBar — 진행 현황 바 (#603 [2] append 정합).
 *
 * 테이블 메뉴 화면 하단에 부착되어 "내가 이 세션에 시킨 것 + 결제"를 메뉴 맥락에서 연결한다.
 * 미결제 후불 주문서(append=Open 1건)가 존재할 때만 노출된다.
 *
 * ## §0(B)(E) 트리거 / 건수
 *   - 노출 조건: 미결제 후불 주문서 존재 (`useSessionUnpaidPostpay !== null`).
 *     `count>=2`(N 주문) 가 아니다 — append 모델은 count 항상 1(dead path 정정).
 *   - "진행 중 {N}건": **N = items.length(라인 수)**. 미결제 건수 count 가 아니다.
 *
 * ## 2단 스택 / 확장 (§3 [2])
 *   - 장바구니에 담는 중(`cartHasItems`): 진행바는 **슬림 행**만 (그 아래 기존 장바구니
 *     "주문하기" 바와 2단 스택 — 지금 담는 것 ↔ 이미 시킨 것 시각 분리). 결제 CTA 미노출.
 *   - 장바구니 비어있음: 진행바가 **"결제하기" CTA 까지 확장**.
 *   - 행 탭 → 세션 시트([3]) / "결제하기" → 전체결제([4]) 직행.
 *
 * 세션 타이머는 기존 `SessionControlBar`(메뉴 헤더)가 담당 — 본 바는 중복 생성하지 않는다.
 *
 * @see src/hooks/useSessionUnpaidPostpay.ts (노출 데이터 출처)
 * @see src/components/session/SessionOrdersSheet.tsx ([3] 행 탭 진입)
 * @see src/components/payment/ChargeSessionModal.tsx ([4] 결제 CTA 진입)
 * @see docs/plans/2026-06-04-postpay-table-order-ux-redesign-design.md §3 [2]
 */
import { ChevronRight, Receipt } from 'lucide-react';
import type { Currency } from '@/types';
import { formatCurrency } from '@/utils/currency';
import { useSafeArea } from '@/hooks/useSafeArea';

export interface SessionProgressBarLabels {
  /** 진행 현황 본문 — {count} = 라인 수. 예: "진행 중 {count}건" */
  progressLabel: string;
  /** 결제 CTA (장바구니 비어있을 때 확장 노출). 예: "결제하기" */
  payButton: string;
}

export interface SessionProgressBarProps {
  /** 진행 중 라인 수 (= Open 주문서 items.length). 0 이면 미렌더. */
  itemCount: number;
  /** 미결제 후불 합계 (서버 권위 문자열) */
  totalAmount: string;
  /** 통화 (금액 포맷) */
  currency: Currency;
  /** 장바구니에 담는 중 여부 — true 면 슬림 행(결제 CTA 미노출), false 면 결제 CTA 확장 */
  cartHasItems: boolean;
  /** i18n 라벨 */
  labels: SessionProgressBarLabels;
  /** 진행 행 탭 → 세션 시트([3]) 오픈 */
  onOpenSheet: () => void;
  /** 결제 CTA → 전체결제([4]) 직행 */
  onCharge: () => void;
}

export function SessionProgressBar({
  itemCount,
  totalAmount,
  currency,
  cartHasItems,
  labels,
  onOpenSheet,
  onCharge,
}: SessionProgressBarProps) {
  // #648(codex Important): 장바구니 비어있을 때(footer 미렌더) 진행바가 도크 최하단 요소 —
  // iOS 홈 인디케이터에 결제 CTA 가 깔리지 않도록 safe-area 하단 인셋을 CTA padding 에 가산.
  // (cartHasItems=true 면 아래 장바구니 푸터가 최하단이라 그쪽이 인셋 담당)
  const { bottomCSS: safeAreaBottom } = useSafeArea();

  // 미결제 라인 0건 → 미렌더 (미결제 없음 — 호출자가 data null 로 게이팅하나 방어).
  if (itemCount <= 0) return null;

  const numericTotal = Number(totalAmount);
  const totalDisplay = formatCurrency(
    Number.isFinite(numericTotal) ? numericTotal : 0,
    currency,
  );
  const progressText = labels.progressLabel.replace('{count}', String(itemCount));

  return (
    <div
      data-testid="session-progress-bar"
      // #634: 진행바는 일반 flow(z-auto)라 하단 fixed 네비(z-50)에 가려진다. 진행바 z 를 올리면
      // ChargeSessionModal·CartModal 등 z-50 모달 위로 떠버리는 역전이 생기므로(진행바는 모달이
      // 열려도 잔존), z 를 올리지 않고 PickupCartNav 를 빈 카트 테이블 모드에서 미렌더해 occlusion 을
      // 제거한다(아래 결제 CTA 가 하단을 점유). 진행바는 모달 아래(z-auto)로 유지된다.
      // viewport 바닥 고정은 PickupMenu 의 하단 도크(sticky bottom-0, pickup-bottom-dock)가 담당
      // — 메뉴가 길어도 스크롤 없이 결제 CTA 가 보인다(#634 z 정책은 그대로).
      className="bg-background border-t border-border"
    >
      {/* 진행 현황 슬림 행 — 탭하면 세션 시트([3]) 오픈 */}
      <button
        type="button"
        data-testid="session-progress-row"
        onClick={onOpenSheet}
        className="w-full flex items-center justify-between gap-3 px-[min(4vw,1rem)] py-3 text-left hover:bg-muted/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        <div className="flex items-center gap-2 min-w-0">
          <Receipt className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
          <span className="text-sm font-medium text-foreground truncate">
            {progressText}
          </span>
          <span className="text-sm text-muted-foreground shrink-0">
            · {totalDisplay}
          </span>
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      </button>

      {/* 장바구니 비어있음 → 결제 CTA 확장. 담는 중이면 기존 장바구니 바가 결제 동선 담당.
          최하단 요소라 safe-area 인셋을 pb-3 에 가산(#648). */}
      {!cartHasItems && (
        <div
          data-testid="session-progress-pay-wrap"
          className="px-[min(4vw,1rem)] pb-3"
          style={{ paddingBottom: `calc(0.75rem + ${safeAreaBottom})` }}
        >
          <button
            type="button"
            data-testid="session-progress-pay"
            onClick={onCharge}
            className="w-full h-[48px] rounded-[8px] bg-primary text-white text-[15px] font-bold hover:bg-primary-hover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          >
            {labels.payButton}
          </button>
        </div>
      )}
    </div>
  );
}

export default SessionProgressBar;
